import CreateEntityModal, { Entity } from '@/components/CreateEntityButton/CreateEntityModal';
import Input from '@/components/Input';
import StyledModal from '@/components/StyledModal';
import {
  CATEGORY_COLORS,
  CATEGORY_ICON_GROUPS,
  IconGroup,
} from '@/constants/category';
import { INSERT_CATEGORY_ONE } from '@/gql/category';
import { CreateCategoryPayload, CreateCategoryResponse } from '@/gql/category/types';
import { keys } from '@/gql/global/keys';
import { APIError } from '@/gql/global/types';
import { RoutesList } from '@/routes/routesList';
import { cleanCategoryForMutation } from '@/services/categories';
import { useCategories, useUpdateCategoryMutation } from '@/services/categories/hooks';
import { fetchData } from '@/services/graphql';
import { useUserInfo } from '@/services/user/hooks';
import { useActionsCategories } from '@/stores/useActionsCategories';
import { Category } from '@/types/category';
import { getRandomCategoryImage, refetchCategoryAndBlocks } from '@/utils/category';
import { getColorFromToken, lightenDarkenColor } from '@/utils/color';
import { getReservedCategoryNames } from '@/utils/index';
import rem from '@/utils/rem';
import { invalidateQueries } from '@/utils/tanStackQuery';
import { trackCategoryCreated } from '@/utils/tracking';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormLabel,
  HStack,
  Icon,
  ModalBody,
  ModalFooter,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Children, useCallback, useEffect, useMemo, useRef, useState, SVGProps } from 'react';
import { Controller, useForm, Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

const CreateCategoryFormSchema = (t: (key: string) => string) => {
  const reservedNames = getReservedCategoryNames();

  return z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t('validation.nameRequired') })
      .max(50, { message: t('validation.nameMaxLength') })
      .refine((data) => !reservedNames.includes(data.toLowerCase()), {
        message: t('validation.reservedName'),
      })
      .refine((data) => !/^\d+$/.test(data), {
        message: t('validation.onlyNumbers'),
      }),
    color: z.string().min(3, { message: t('validation.colorRequired') }),
    icon: z.string().min(3, { message: t('validation.iconRequired') }),
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CategoryFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'Name requires at least 1 character' })
    .max(50, { message: 'Category name must be 50 characters or less' })
    .refine((data) => !getReservedCategoryNames().includes(data.toLowerCase()), {
      message: 'This category name is reserved. Please enter a new name and try again',
    })
    .refine((data) => !/^\d+$/.test(data), {
      message: 'Name cannot be a string of only numbers',
    }),
  color: z.string().min(3, { message: 'Color is required' }),
  icon: z.string().min(3, { message: 'Icon is required' }),
});

type FormData = z.infer<typeof CategoryFormSchema>;

// Icon size constants
const ICON_SIZE = 24;
const ICON_CONTAINER_SIZE = 42;
const GROUP_SELECTOR_SIZE = 42;

// Group Selector Icon Component
type GroupSelectorProps = {
  group: IconGroup;
  isSelected: boolean;
  onClick: () => void;
  showSeparator?: boolean;
  label?: string;
};

const GroupSelector = ({ group, isSelected, onClick, showSeparator = false, label }: GroupSelectorProps) => {
  const separatorBg = useColorModeValue('gray.400', 'white');
  const separatorOpacity = useColorModeValue(0.55, 0.3);

  const selectedBg = useColorModeValue('button-bg-primary', 'white');
  const selectedBorderColor = useColorModeValue('gray.900', 'white');
  const selectedBorderWidth = useColorModeValue('2px', '1.5px');
  const selectedIconColor = useColorModeValue('gray.900', 'gray.800');

  const unselectedBorderColor = useColorModeValue('border-secondary', 'border-secondary');
  const hoverBgUnselected = useColorModeValue('cyan.100', 'whiteAlpha.100');
  const hoverBorderUnselected = useColorModeValue('cyan.300', 'whiteAlpha.400');
  const hoverBgSelected = useColorModeValue('cyan.500', 'white');
  const hoverBorderSelected = useColorModeValue('gray.900', 'white');

  return (
    <HStack alignItems="center" spacing={0}>
      <Tooltip hasArrow label={label ?? group.label} placement="top">
        <Flex
          position="relative"
          alignItems="center"
          justifyContent="center"
          width={rem(GROUP_SELECTOR_SIZE)}
          height={rem(GROUP_SELECTOR_SIZE)}
          border={`${selectedBorderWidth} solid`}
          borderColor={isSelected ? selectedBorderColor : unselectedBorderColor}
          borderRadius="full"
          _hover={{
            backgroundColor: isSelected ? hoverBgSelected : hoverBgUnselected,
            borderColor: isSelected ? hoverBorderSelected : hoverBorderUnselected,
          }}
          cursor="pointer"
          transition="all 0.2s ease-out"
          backgroundColor={isSelected ? selectedBg : 'transparent'}
          onClick={onClick}
        >
          <Icon
            as={group.selectorIcon}
            width={rem(ICON_SIZE)}
            height={rem(ICON_SIZE)}
            color={isSelected ? selectedIconColor : 'icon-secondary'}
            transition="color 0.2s"
          />
        </Flex>
      </Tooltip>
      {showSeparator && (
        <Box
          width="1px"
          height={rem(24)}
          opacity={separatorOpacity}
          backgroundColor={separatorBg}
          marginX={rem(12)}
        />
      )}
    </HStack>
  );
};

// Icon Button Component
type IconButtonProps = {
  slug: string;
  icon: React.FunctionComponent<SVGProps<SVGSVGElement>>;
  control: Control<FormData>;
};

const IconButton = ({ slug, icon, control }: IconButtonProps) => {
  const selectedBg = useColorModeValue('button-bg-primary', 'white');
  const hoverUnselected = useColorModeValue('cyan.100', 'whiteAlpha.100');
  const hoverSelected = useColorModeValue('cyan.500', 'white');
  const selectedIconColor = useColorModeValue('gray.900', 'gray.800');

  return (
    <Controller
      name="icon"
      control={control}
      render={({ field: { ref, value, onChange } }) => (
        <Flex
          position="relative"
          alignItems="center"
          justifyContent="center"
          width={rem(ICON_CONTAINER_SIZE)}
          height={rem(ICON_CONTAINER_SIZE)}
          borderRadius="full"
          _hover={{
            backgroundColor: value === slug ? hoverSelected : hoverUnselected,
          }}
          cursor="pointer"
          transition="all 0.2s"
          backgroundColor={value === slug ? selectedBg : 'transparent'}
        >
          <Checkbox
            ref={ref}
            position="absolute"
            zIndex={2}
            width="full"
            height="full"
            opacity="0"
            isChecked={value === slug}
            onChange={(e) => (e.target.checked ? onChange(slug) : onChange(''))}
          />
          <Icon
            as={icon}
            width={rem(ICON_SIZE)}
            height={rem(ICON_SIZE)}
            color={value === slug ? selectedIconColor : 'icon-secondary'}
            transition="color 0.2s"
          />
        </Flex>
      )}
    />
  );
};

// Icon Group Section Component
type IconGroupSectionProps = {
  group: IconGroup;
  control: Control<FormData>;
  sectionRef: (el: HTMLDivElement | null) => void;
  showDivider?: boolean;
  label?: string;
};

const IconGroupSection = ({ group, control, sectionRef, showDivider = true, label }: IconGroupSectionProps) => {
  const sectionDividerColor = useColorModeValue('gray.400', 'white');
  const sectionDividerOpacity = useColorModeValue(0.65, 0.15);

  return (
    <Box
      ref={sectionRef}
      paddingBottom={rem(24)}
      data-group-id={group.id}
    >
      <Text
        marginBottom={rem(12)}
        color="text-tertiary"
        fontSize="xs"
        fontWeight="900"
        textTransform="uppercase"
      >
        {label ?? group.label}
      </Text>
      <HStack flexWrap="wrap" gap={rem(2)} spacing={rem(8)}>
        {Object.entries(group.icons).map(([slug, icon]) => (
          <IconButton
            key={slug}
            slug={slug}
            icon={icon}
            control={control}
          />
        ))}
      </HStack>
      {showDivider && (
        <Divider
          marginTop={rem(16)}
          opacity={sectionDividerOpacity}
          borderColor={sectionDividerColor}
        />
      )}
    </Box>
  );
};

type Props = {
  category?: Category;
  isOpen: boolean;
  onClose: (category?: Category) => void;
  invalidateQueries?: () => void;
  isEditMode?: boolean;
  showEntityModalOnCreate?: boolean;
};

function CreateEditCategoryModal({
  category,
  isOpen,
  onClose,
  isEditMode = false,
  showEntityModalOnCreate = false,
}: Props) {
  const { t } = useTranslation('CreateEditCategoryModal');
  const { data: userInfo } = useUserInfo();
  const formRef = useRef<HTMLFormElement>(null);
  const iconsContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const queryClient = useQueryClient();

  const [entity, setEntity] = useState<Entity | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(CATEGORY_ICON_GROUPS[0]?.id || '');
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastIconSyncedRef = useRef<string | null>(null);
  const selectedGroupIdRef = useRef<string>(CATEGORY_ICON_GROUPS[0]?.id || '');
  const isProgrammaticScrollRef = useRef(false);

  // Refs for each group section
  const groupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const categories = useCategories();
  const nextOrder = useMemo(
    () => (categories.data?.category || []).reduce((acc, c) => Math.max(acc, c.order ?? 0), 0) + 1,
    [categories],
  );
  const selectedCategory = useActionsCategories((state) => state.selectedCategory);
  const updateSelectedCategory = useActionsCategories((state) => state.updateSelectedCategory);

  const { isOpen: isEntityModalOpen, onOpen: onEntityModalOpen, onClose: onEntityModalClose } = useDisclosure();

  const localizedSchema = useMemo(() => CreateCategoryFormSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setFocus,
    formState: { errors, isDirty, isValid },
  } = useForm<FormData>({
    defaultValues: {
      name: category?.name || '',
      color: category?.color || '',
      icon: category?.icon || '',
    },
    resolver: zodResolver(localizedSchema),
    mode: 'onChange',
  });

  const characterCount = watch('name')?.trim().length;
  const selectedIcon = watch('icon');

  // Keep refs in sync so scroll handler always sees current values (effect runs only on isOpen)
  selectedGroupIdRef.current = selectedGroupId;
  isProgrammaticScrollRef.current = isProgrammaticScroll;

  // Helper function to scroll to a group
  const scrollToGroup = useCallback((groupId: string, behavior: ScrollBehavior = 'auto') => {
    const element = groupRefs.current[groupId];
    const container = iconsContainerRef.current;

    if (element && container) {
      isProgrammaticScrollRef.current = true;
      setIsProgrammaticScroll(true);

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollOffset = container.scrollTop + (elementRect.top - containerRect.top);

      container.scrollTo({
        top: scrollOffset,
        behavior,
      });

      if (behavior === 'smooth') {
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isProgrammaticScrollRef.current = false;
          setIsProgrammaticScroll(false);
        }, 400);
      }
    }
  }, []);

  // Update selected group ONLY when the user selects a new icon (not when nav click or scroll ends)
  useEffect(() => {
    if (!selectedIcon) {
      lastIconSyncedRef.current = null;
      return;
    }
    // Only sync nav to icon's group when the icon selection actually changed
    if (lastIconSyncedRef.current === selectedIcon) return;

    const groupWithIcon = CATEGORY_ICON_GROUPS.find((group) =>
      Object.keys(group.icons).includes(selectedIcon)
    );
    if (groupWithIcon) {
      lastIconSyncedRef.current = selectedIcon;
      selectedGroupIdRef.current = groupWithIcon.id;
      setSelectedGroupId(groupWithIcon.id);
    }
  }, [selectedIcon]);

  // When opening in edit mode: highlight in the nav the group that contains the category's icon and scroll to that section.
  // State/ref are set immediately so the bar shows the correct group from the first frame; scroll is delayed slightly so the DOM is ready.
  useEffect(() => {
    if (!isOpen || !isEditMode || !category?.icon) return;

    const iconKey = category.icon;
    const groupWithIcon = CATEGORY_ICON_GROUPS.find((group) =>
      Object.keys(group.icons).includes(iconKey)
    );

    if (groupWithIcon) {
      selectedGroupIdRef.current = groupWithIcon.id;
      setSelectedGroupId(groupWithIcon.id);
      lastIconSyncedRef.current = iconKey;

      const scrollAndRelease = () => {
        scrollToGroup(groupWithIcon.id);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isProgrammaticScrollRef.current = false;
          setIsProgrammaticScroll(false);
        }, 400);
      };

      setTimeout(scrollAndRelease, 50);
    }
  }, [isOpen, isEditMode, category?.icon, scrollToGroup]);

  // Scroll spy: highlight in the top nav the section currently in view while scrolling.
  // - Listener is attached only when modal opens; we defer so iconsContainerRef is set (modal DOM ready).
  // - Handler reads refs (isProgrammaticScrollRef, selectedGroupIdRef) so it never needs to re-subscribe.
  useEffect(() => {
    if (!isOpen) return;

    let rafId: number | null = null;
    let listenerAttached = false;
    let container: HTMLDivElement | null = null;

    const handleScroll = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (isProgrammaticScrollRef.current) return;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const threshold = 100;

        let activeGroup: string | null = null;
        let closestDistance = Infinity;

        for (const group of CATEGORY_ICON_GROUPS) {
          const element = groupRefs.current[group.id];
          if (element) {
            const elementRect = element.getBoundingClientRect();
            const distance = Math.abs(elementRect.top - containerTop);
            if (elementRect.top <= containerTop + threshold && elementRect.bottom > containerTop) {
              if (distance < closestDistance) {
                closestDistance = distance;
                activeGroup = group.id;
              }
            }
          }
        }

        if (!activeGroup) {
          for (const group of CATEGORY_ICON_GROUPS) {
            const element = groupRefs.current[group.id];
            if (element) {
              const elementRect = element.getBoundingClientRect();
              if (elementRect.top < containerRect.bottom && elementRect.bottom > containerTop) {
                activeGroup = group.id;
                break;
              }
            }
          }
        }

        if (!activeGroup) activeGroup = CATEGORY_ICON_GROUPS[0]?.id || null;

        const currentGroupId = selectedGroupIdRef.current;
        if (activeGroup && activeGroup !== currentGroupId) {
          const isAtBottom = Math.abs(container.scrollHeight - container.clientHeight - container.scrollTop) < 10;
          if (isAtBottom) {
            let lastVisibleGroup = activeGroup;
            for (let i = CATEGORY_ICON_GROUPS.length - 1; i >= 0; i--) {
              const group = CATEGORY_ICON_GROUPS[i];
              const el = groupRefs.current[group.id];
              if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.top < containerRect.bottom - 50) {
                  lastVisibleGroup = group.id;
                  break;
                }
              }
            }
            selectedGroupIdRef.current = lastVisibleGroup;
            setSelectedGroupId(lastVisibleGroup);
          } else {
            selectedGroupIdRef.current = activeGroup;
            setSelectedGroupId(activeGroup);
          }
        }
      });
    };

    const attachListener = () => {
      container = iconsContainerRef.current;
      if (!container) return;
      listenerAttached = true;
      container.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    };

    const timeoutId = setTimeout(attachListener, 100);

    return () => {
      clearTimeout(timeoutId);
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (listenerAttached && container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isOpen]);

  // Handle group selector click - highlight and scroll to section
  const handleGroupSelect = useCallback((groupId: string) => {
    isProgrammaticScrollRef.current = true;
    setIsProgrammaticScroll(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    setSelectedGroupId(groupId);
    selectedGroupIdRef.current = groupId;
    scrollToGroup(groupId, 'auto');

    scrollTimeoutRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      setIsProgrammaticScroll(false);
    }, 400);
  }, [scrollToGroup]);

  const { mutate: createCategory } = useMutation({
    mutationFn: (payload: CreateCategoryPayload) => fetchData<CreateCategoryResponse>(INSERT_CATEGORY_ONE, payload),

    onSuccess: async ({ insertCategoryOne }) => {
      toast({
        title: t('toasts.categoryCreated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      updateSelectedCategory(insertCategoryOne);

      if (showEntityModalOnCreate) {
        setEntity({
          name: insertCategoryOne.name,
          id: insertCategoryOne.id,
          slug: `${RoutesList.CategoryManagerPage}/${insertCategoryOne.id}`,
          type: 'Category',
        });
        onEntityModalOpen();
      }

      invalidateQueries([keys.categories.all.queryKey]);
      onCloseModal(insertCategoryOne);

      await queryClient.invalidateQueries({
        predicate({ queryKey }) {
          const name = queryKey[0];
          return typeof name === 'string' && name.startsWith('my-plan');
        },
      });
    },

    onError: (error: APIError) => {
      let title = error?.response?.errors?.[0]?.message;

      const code = error.response.errors[0].extensions.code;
      if (code === 'constraint-violation') {
        title = t('toasts.duplicateName');
      }

      toast({
        title,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const updateCategory = useUpdateCategoryMutation({
    onSuccess: async (_, variables) => {
      refetchCategoryAndBlocks();
      invalidateQueries([keys.project.category(category?.id ?? '').queryKey]);

      if (selectedCategory?.id === variables.id) {
        updateSelectedCategory(variables);
      }

      toast({
        title: t('toasts.categoryUpdated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onCloseModal();
    },
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (isEditMode) {
        if (!category?.id) return;

        const updatedCategoryData = {
          ...cleanCategoryForMutation(category),
          ...data,
        } as Category;
        updatedCategoryData.id = category?.id;
        updateCategory.mutateAsync(updatedCategoryData);
        return;
      }

      if (userInfo?.me?.email && !getReservedCategoryNames().includes(data?.name?.toLowerCase())) {
        trackCategoryCreated();
        return createCategory({
          id: uuidv4(),
          name: data.name,
          color: data.color,
          icon: data.icon,
          ninetyDayGoal: null,
          oneYearGoal: null,
          order: nextOrder,
          selectedImageUrl: getRandomCategoryImage()?.name,
          ultimatePurpose: null,
          ultimateVision: null,
          role: null,
          isArchived: false,
        });
      }

      return toast({
        title: t('toasts.reservedNameError', {
          name: data?.name,
          anOrA: data?.name?.toLowerCase().startsWith('u') ? 'n' : '',
        }),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
    [category, createCategory, isEditMode, nextOrder, t, toast, updateCategory, userInfo?.me?.email],
  );

  const onCloseModal = useCallback(
    (category?: Category) => {
      reset();
      setSelectedGroupId(CATEGORY_ICON_GROUPS[0]?.id || '');
      setIsProgrammaticScroll(false);
      selectedGroupIdRef.current = CATEGORY_ICON_GROUPS[0]?.id || '';
      isProgrammaticScrollRef.current = false;
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      onClose(category);
    },
    [onClose, reset],
  );

  const submitForm = () => formRef?.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));

  useEffect(() => {
    if (isEditMode) {
      reset({
        name: category?.name || '',
        color: category?.color || '',
        icon: category?.icon || '',
      });
    }
  }, [category?.color, category?.icon, category?.name, isEditMode, reset]);

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => setFocus('name'), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, setFocus]);

  const iconNavBottomBorderColor = useColorModeValue('gray.400', 'whiteAlpha.300');
  const underIconNavDividerColor = useColorModeValue('gray.400', 'white');
  const underIconNavDividerOpacity = useColorModeValue(0.72, 0.5);
  const iconsScrollThumbBg = useColorModeValue('rgba(0, 0, 0, 0.22)', 'rgba(255, 255, 255, 0.3)');

  return (
    <>
      <StyledModal
        isOpen={isOpen}
        onClose={onCloseModal}
        title={!isEditMode ? t('title.createNewCategory') : t('title.editCategory')}
        size="6xl"
      >
        <ModalBody flexDirection="column" display="flex" maxHeight="75vh" padding={0}>
          <form ref={formRef} onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {/* Scrollable top section: Name & Color */}
            <Box paddingTop={rem(24)} paddingX={rem(24)}>
            <VStack justifyContent="start">
              <Controller
                name="name"
                control={control}
                render={({ field: { value, onChange, ref } }) => (
                  <Input
                    ref={ref}
                    label={t('labels.name')}
                    type="text"
                    autoComplete="off"
                    value={value}
                    onChange={onChange}
                    error={errors?.name?.message}
                      formControlWidth="full"
                      _hover={{ boxShadow: 'none' }}
                      _focusVisible={{ boxShadow: 'none', borderColor: 'cyan.400 !important' }}
                    _invalid={{ boxShadow: 'none', borderBottom: '1px solid red' }}
                  />
                )}
              />
              <Text
                width="full"
                color="text-tertiary"
                  fontSize="xs"
                fontWeight={500}
                textAlign="left"
                textTransform="uppercase"
              >
                {t('labels.characterLimit')}
              </Text>
            </VStack>

            <FormLabel
              margin={`${rem(24)} 0 ${rem(16)}`}
                color="text-tertiary"
              fontSize="xs"
              fontWeight="900"
              textTransform="uppercase"
            >
              {t('labels.color')}
            </FormLabel>

            <Flex flexWrap="wrap" gap="0.25rem 0" marginBottom={rem(24)} role="radiogroup">
              {Children.toArray(
                CATEGORY_COLORS.map((color) => (
                  <Controller
                    name="color"
                    control={control}
                    render={({ field: { ref, value, onChange } }) => (
                      <Flex
                        position="relative"
                        alignItems="center"
                        justifyContent="center"
                        width={rem(32)}
                        height={rem(32)}
                        margin="0.25rem 0.8125rem 0.25rem 0"
                        border={value === color ? '4px solid' : 'none'}
                        borderColor="stroke-secondary"
                        borderRadius="full"
                        _hover={{ backgroundColor: lightenDarkenColor(getColorFromToken(color), 30) }}
                        backgroundColor={getColorFromToken(color)}
                      >
                        <Checkbox
                          ref={ref}
                          zIndex={10}
                          width="100%"
                          height="100%"
                          opacity="0"
                          isChecked={value === color}
                          onChange={() => onChange(color)}
                        />
                      </Flex>
                    )}
                  />
                )),
              )}
            </Flex>

              {/* Sticky Icon Group Selector */}
              <FormLabel
                marginBottom={rem(16)}
                color="text-tertiary"
                fontSize="xs"
                fontWeight="900"
                textTransform="uppercase"
              >
              {t('labels.icon')}
            </FormLabel>

              <HStack
                flexWrap="wrap"
                gap={0}
                marginBottom={rem(6)}
                paddingBottom={rem(12)}
                borderBottomWidth="1px"
                borderBottomColor={iconNavBottomBorderColor}
                spacing={0}
              >
                {CATEGORY_ICON_GROUPS.map((group, index) => (
                  <GroupSelector
                    key={group.id}
                    group={group}
                    isSelected={selectedGroupId === group.id}
                    onClick={() => handleGroupSelect(group.id)}
                    showSeparator={index < CATEGORY_ICON_GROUPS.length - 1}
                    label={t(`iconGroups.${group.id}`)}
                  />
                ))}
              </HStack>

              <Divider
                opacity={underIconNavDividerOpacity}
                borderColor={underIconNavDividerColor}
                marginY={rem(24)}
              />
            </Box>

            {/* Scrollable Icons Container */}
            <Box
              ref={iconsContainerRef}
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: iconsScrollThumbBg,
                  borderRadius: '2px',
                },
              }}
              flex={1}
              overflowY="auto"
              paddingBottom={rem(16)}
              paddingX={rem(24)}
            >
              {CATEGORY_ICON_GROUPS.map((group, index) => (
                <IconGroupSection
                  key={group.id}
                  group={group}
                  control={control}
                  sectionRef={(el) => {
                    groupRefs.current[group.id] = el;
                  }}
                  showDivider={index < CATEGORY_ICON_GROUPS.length - 1}
                  label={t(`iconGroups.${group.id}`)}
                />
              ))}
            </Box>
          </form>
        </ModalBody>

        <ModalFooter
          borderColor="whiteAlpha.300"
        >
          <Button onClick={() => onCloseModal()} size="lg" variant="secondary">
            {t('buttons.cancel')}
          </Button>
          <Button
            isDisabled={!isDirty || !isValid || characterCount === 0}
            onClick={submitForm}
            size="lg"
            variant="primary"
          >
            {!isEditMode ? t('buttons.createCategory') : t('buttons.updateCategory')}
          </Button>
        </ModalFooter>
      </StyledModal>

      {entity && <CreateEntityModal isOpen={isEntityModalOpen} onCancel={onEntityModalClose} entity={entity} />}
    </>
  );
}

export default CreateEditCategoryModal;

